import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CreditCard, ArrowRight, Lock, 
  Banknote, Building2, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * AvanceImmediatePromo - Reusable promo block for Avance Immédiate SAP
 * 
 * Updated to be legally accurate and transparent per URSSAF guidelines.
 * 
 * Variants:
 * - 'full': Complete section with clear information (for homepage, main pages)
 * - 'compact': Smaller version for service/town pages
 * - 'inline': Single line badge for hero areas
 * - 'mini': Minimal text link for tight spaces
 */
const AvanceImmediatePromo = ({ variant = 'full', town = null }) => {
  const locationText = town ? `à ${town}` : '';

  // Mini variant - just a text link
  if (variant === 'mini') {
    return (
      <Link 
        to="/avance-immediate" 
        className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium text-sm"
        data-testid="avance-mini-link"
      >
        <CreditCard size={14} />
        Option Avance immédiate (si éligible)
        <ArrowRight size={14} />
      </Link>
    );
  }

  // Inline variant - single line badge for hero areas
  if (variant === 'inline') {
    return (
      <div className="flex items-center justify-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-4 py-2" data-testid="avance-inline-badge">
        <Building2 size={16} className="text-blue-600" />
        <span>
          <strong>Avance immédiate Urssaf</strong> : payez ≈ 50% si éligible
        </span>
        <Link to="/avance-immediate" className="text-blue-600 hover:text-blue-800 font-medium underline ml-1">
          En savoir plus
        </Link>
      </div>
    );
  }

  // Compact variant - for service/town pages
  if (variant === 'compact') {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-200 rounded-2xl p-6 shadow-sm" data-testid="avance-compact-promo">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-gray-900 text-lg mb-1">
              Avance immédiate Urssaf {locationText}
            </h3>
            <p className="text-gray-600 text-sm">
              Service officiel : si éligible et après activation, ne payez que ≈ 50% (reste à charge).
            </p>
          </div>
        </div>

        {/* Key points */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-white px-3 py-1.5 rounded-full border border-slate-200">
            <Building2 size={12} className="text-blue-500" />
            Service Urssaf officiel
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-white px-3 py-1.5 rounded-full border border-slate-200">
            <Lock size={12} className="text-slate-500" />
            Activation sur particulier.urssaf.fr
          </div>
        </div>

        {/* CTA */}
        <Link 
          to="/avance-immediate"
          className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-sm group"
          data-testid="avance-compact-link"
        >
          Comment ça marche ?
          <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    );
  }

  // Full variant - complete section with clear information
  return (
    <section className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 text-white overflow-hidden relative" data-testid="avance-full-promo">
      {/* Subtle pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M20 20.5V18H0v-2h20v-2.5l4 3.5-4 3.5z"/%3E%3C/g%3E%3C/svg%3E")'}}></div>
      </div>
      
      <div className="max-w-3xl mx-auto relative">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 bg-blue-500 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-500/30">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold mb-1">
              Avance immédiate : ne payez que le reste à charge
            </h2>
            <div className="flex items-center gap-2 text-blue-300 text-sm">
              <span>Service officiel de l'Urssaf</span>
            </div>
          </div>
        </div>

        {/* Main text */}
        <p className="text-slate-300 mb-6 text-lg leading-relaxed">
          L'Avance immédiate est un service gratuit proposé par l'Urssaf. Si éligible et après activation sur 
          <a href="https://www.particulier.urssaf.fr/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 mx-1">
            particulier.urssaf.fr
          </a>, 
          vous ne payez que <strong className="text-white">≈ 50% (reste à charge)</strong>. Le crédit d'impôt est déduit directement.
        </p>

        {/* Key info */}
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <Lock className="w-5 h-5 text-blue-400 flex-shrink-0" />
            <span className="text-sm text-slate-200">Activation et mandat SEPA sur le portail Urssaf</span>
          </div>
          <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <Banknote className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            <span className="text-sm text-slate-200">Paiement classique toujours disponible</span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/avance-immediate">
            <Button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold w-full sm:w-auto shadow-lg shadow-blue-500/25" data-testid="avance-full-cta">
              Comment ça marche ?
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
          <Link to="/devis">
            <Button variant="outline" className="border-2 border-slate-500 text-white hover:bg-white/10 w-full sm:w-auto" data-testid="avance-full-devis">
              Demander un devis
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AvanceImmediatePromo;
